/**
 * Hit testing.
 *
 * Done in screen space on purpose: field space is normalised per-axis, so a
 * circle in field space is an ellipse on screen for any non-square surface.
 */

import type { Entity, PoseMap, Shape, SportPack, Vec2 } from '../core/index.ts';
import { findEntityType, findRole, pointToSegmentDistance } from '../core/index.ts';
import { POSITION_FONT, POSITION_OFFSET, positionLabel } from './renderer.ts';
import { toScreen, px, type Viewport } from './viewport.ts';

/** Topmost entity under the point, or null. `slopPx` widens the target for touch. */
export function hitTestEntity(
  pack: SportPack,
  entities: readonly Entity[],
  poses: PoseMap,
  screenPoint: Vec2,
  vp: Viewport,
  slopPx = 0,
): Entity | null {
  let best: Entity | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const entity of entities) {
    const position = poses[entity.id];
    if (!position) continue;
    const type = findEntityType(pack, entity.typeId);
    if (!type || !type.draggable) continue;

    const centre = toScreen(position, vp);
    const distance = Math.hypot(screenPoint.x - centre.x, screenPoint.y - centre.y);
    const radius = px(type.radius, vp) + slopPx;
    // Ties go to the smaller marker (the ball sitting on a player).
    if (distance <= radius && distance - radius < bestDistance) {
      bestDistance = distance - radius;
      best = entity;
    }
  }
  return best;
}

/** Topmost shape whose stroke passes within `slopPx` of the point, or null. */
export function hitTestShape(
  shapes: readonly Shape[],
  screenPoint: Vec2,
  vp: Viewport,
  slopPx = 8,
): Shape | null {
  for (let i = shapes.length - 1; i >= 0; i -= 1) {
    const shape = shapes[i]!;
    const points = shape.points.map((p) => toScreen(p, vp));
    const tolerance = px(shape.width, vp) / 2 + slopPx;
    for (let j = 1; j < points.length; j += 1) {
      if (pointToSegmentDistance(screenPoint, points[j - 1]!, points[j]!) <= tolerance) return shape;
    }
  }
  return null;
}

/**
 * The player whose position label (drawn under the cap) is under the point, or
 * null. The label's box is estimated from its font size and length — close
 * enough to catch a double-click, and padded by `slopPx` for touch.
 */
export function hitTestPositionLabel(
  pack: SportPack,
  entities: readonly Entity[],
  poses: PoseMap,
  screenPoint: Vec2,
  vp: Viewport,
  slopPx = 0,
): Entity | null {
  for (const entity of entities) {
    const position = poses[entity.id];
    const text = positionLabel(entity, findRole(pack, entity.roleId));
    const type = findEntityType(pack, entity.typeId);
    if (!position || !text || !type) continue;

    const centre = toScreen(position, vp);
    const radius = px(type.radius, vp);
    const font = radius * POSITION_FONT;
    const halfWidth = Math.max(font * 1.5, text.length * font * 0.34) + slopPx;
    const top = centre.y + radius * POSITION_OFFSET - slopPx;
    const bottom = centre.y + radius * POSITION_OFFSET + font * 1.15 + slopPx;
    if (Math.abs(screenPoint.x - centre.x) <= halfWidth && screenPoint.y >= top && screenPoint.y <= bottom) {
      return entity;
    }
  }
  return null;
}
